import {
  type SnapRoundingOptions,
  snapRoundPerUnit,
} from '@calculations/round/decimal';
import { discountPriceToPriceBook } from '@snap-types/models/price-book';
import type { VisualDiscountPrice } from '@snap-types/models/visual/discount-price';
import type { Decimal } from '@snap-types/types/decimal';

import {
  convertPriceBookToQuantityBreaks,
  omitZeroPriceQuantityBreaks,
} from '../../utilities/quantity-breaks';

export type FixedDiscountCalculateParams = {
  quantity: Decimal;
  allowFallback: boolean;
  discountCode: string;
  discountPrices: VisualDiscountPrice[];
};

export type FixedDiscountCalculateResult = {
  quantity: Decimal;
  discountCode: string;
  unitPrice: Decimal;
  discountPrice: VisualDiscountPrice;
};

export function calculate(
  {
    quantity,
    allowFallback,
    discountCode,
    discountPrices,
  }: FixedDiscountCalculateParams,
  roundingOptions: SnapRoundingOptions,
): FixedDiscountCalculateResult | null {
  const discountPrice = discountPrices.find(
    (price) => price.discount_code === discountCode,
  );

  // No entry in price book for the given code
  if (!discountPrice) {
    return null;
  }

  const quantityBreaks = omitZeroPriceQuantityBreaks(
    convertPriceBookToQuantityBreaks(discountPriceToPriceBook(discountPrice)),
  );

  for (let i = 0; i < quantityBreaks.length; i++) {
    const lowBreak = quantityBreaks[i];

    // Check for the last quantity break
    if (i === quantityBreaks.length - 1) {
      if (allowFallback) {
        // When allowFallback is true, only allow exact match with max break
        if (quantity.eq(lowBreak.quantity)) {
          return {
            quantity,
            discountCode,
            discountPrice,
            unitPrice: snapRoundPerUnit(lowBreak.unitPrice, roundingOptions),
          };
        }
        // If quantity exceeds max break, return null
        return null;
      } else {
        // Original behavior: allow any quantity >= max break
        if (quantity.gte(lowBreak.quantity)) {
          return {
            quantity,
            discountCode,
            discountPrice,
            unitPrice: snapRoundPerUnit(lowBreak.unitPrice, roundingOptions),
          };
        }

        return null;
      }
    }

    // We're not at the last quantity break, so get the next one
    // and see if the requested quantity is between them.
    const highBreak = quantityBreaks[i + 1];

    if (quantity.gte(lowBreak.quantity) && quantity.lt(highBreak.quantity)) {
      return {
        quantity,
        discountCode,
        discountPrice,
        unitPrice: snapRoundPerUnit(lowBreak.unitPrice, roundingOptions),
      };
    }
  }

  return null;
}
